import { useMemo } from 'react';
import { format } from 'date-fns';
import { dateStringToDate } from '@/lib/date-values';
import { useInvoices } from '@/hooks/invoices/useInvoices';
import { useEstimates } from '@/hooks/estimates/useEstimates';
import { useHasPermission } from '@/hooks/usePermissions';

export type ActionItemType = 'invoice' | 'estimate' | 'job';
export type ActionUrgency = 'high' | 'medium' | 'low';

export interface ActionNeededItem {
  id: string;
  type: ActionItemType;
  title: string;
  subtitle: string;
  urgency: ActionUrgency;
  link: string;
  /** Dollars at stake — overdue balance / estimate value — for the ranked list. */
  amount: number;
}

/**
 * "Needs attention" items for the Home widget, from real operations data:
 * overdue invoices (`GET /invoices?overdue=true`) and estimates awaiting
 * approval (`GET /estimates?status=SENT`). Replaces the dead legacy
 * `/api/dashboard/fetchJobData`.
 */
export function useActionNeeded() {
  const canViewInvoices = useHasPermission('invoices:view');
  const canViewEstimates = useHasPermission('estimates:view');
  const invoices = useInvoices(
    { overdue: true, limit: 5, sortBy: 'dueDate', sortDir: 'asc' },
    { enabled: canViewInvoices },
  );
  // Once a customer opens an estimate it moves SENT → VIEWED; FOLLOW_UP and
  // REVISED are also still waiting on a customer decision. Query every
  // actionable state so engagement cannot make the item disappear from Home.
  const sentEstimates = useEstimates({ status: 'SENT', limit: 5 }, { enabled: canViewEstimates });
  const viewedEstimates = useEstimates(
    { status: 'VIEWED', limit: 5 },
    { enabled: canViewEstimates },
  );
  const followUpEstimates = useEstimates(
    { status: 'FOLLOW_UP', limit: 5 },
    { enabled: canViewEstimates },
  );
  const revisedEstimates = useEstimates(
    { status: 'REVISED', limit: 5 },
    { enabled: canViewEstimates },
  );

  const data = useMemo<ActionNeededItem[]>(() => {
    const items: ActionNeededItem[] = [];

    for (const inv of canViewInvoices ? (invoices.data?.invoices ?? []) : []) {
      const name = inv.contact
        ? [inv.contact.firstName, inv.contact.lastName].filter(Boolean).join(' ') ||
          inv.contact.company ||
          ''
        : '';
      items.push({
        id: inv.id,
        type: 'invoice',
        title: `INV ${inv.invoiceNumber ?? ''}${name ? ` · ${name}` : ''}`.trim(),
        subtitle: inv.dueDate
          ? `Overdue · due ${format(dateStringToDate(inv.dueDate)!, 'MMM d')}`
          : 'Overdue',
        urgency: 'high',
        link: `/dashboard/invoices/${inv.id}`,
        amount: inv.balanceDue ?? inv.total ?? 0,
      });
    }

    const estimates = canViewEstimates
      ? [
          sentEstimates.data,
          viewedEstimates.data,
          followUpEstimates.data,
          revisedEstimates.data,
        ].flatMap((response) => response?.data ?? [])
      : [];
    for (const est of estimates) {
      const name = est.customer?.name ?? '';
      items.push({
        id: est.id,
        type: 'estimate',
        title: `EST ${est.estimateNumber ?? ''}${name ? ` · ${name}` : ''}`.trim(),
        subtitle: 'Awaiting approval',
        urgency: 'medium',
        link: `/dashboard/estimates/${est.id}`,
        amount: est.grandTotal ?? 0,
      });
    }

    return items;
  }, [
    canViewEstimates,
    canViewInvoices,
    invoices.data,
    sentEstimates.data,
    viewedEstimates.data,
    followUpEstimates.data,
    revisedEstimates.data,
  ]);

  return {
    hasAccess: canViewInvoices || canViewEstimates,
    data,
    isLoading:
      (canViewInvoices && invoices.isLoading) ||
      (canViewEstimates &&
        (sentEstimates.isLoading ||
          viewedEstimates.isLoading ||
          followUpEstimates.isLoading ||
          revisedEstimates.isLoading)),
    isError:
      (canViewInvoices && invoices.isError) ||
      (canViewEstimates &&
        (sentEstimates.isError ||
          viewedEstimates.isError ||
          followUpEstimates.isError ||
          revisedEstimates.isError)),
    refetch: () => {
      if (canViewInvoices) void invoices.refetch();
      if (canViewEstimates) {
        void sentEstimates.refetch();
        void viewedEstimates.refetch();
        void followUpEstimates.refetch();
        void revisedEstimates.refetch();
      }
    },
  };
}
